import { useState, useEffect, useMemo } from "react";

/**
 * Custom hook for table search and filtering
 * @param {array} data - Array of data to search/filter
 * @param {array} searchFields - Array of field names to search in
 * @param {number} debounceDelay - Debounce delay in milliseconds
 */
const useTableSearch = (data = [], searchFields = [], debounceDelay = 100) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceDelay);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, debounceDelay]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return data;
    }

    const lowerSearchTerm = debouncedSearchTerm.toLowerCase();

    return data.filter((item) => {
      // If no search fields specified, search all string fields
      if (searchFields.length === 0) {
        return Object.values(item).some((value) =>
          String(value).toLowerCase().includes(lowerSearchTerm)
        );
      }

      // Search in specified fields
      return searchFields.some((field) => {
        const value = getNestedValue(item, field);
        return String(value).toLowerCase().includes(lowerSearchTerm);
      });
    });
  }, [data, debouncedSearchTerm, searchFields]);

  return {
    searchTerm,
    setSearchTerm,
    filteredData,
  };
};

/**
 * Get nested object value by dot notation
 * @param {object} obj - Object to search in
 * @param {string} path - Dot notation path (e.g., "user.name")
 */
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc?.[part], obj) || "";
};

export default useTableSearch;